import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  filter,
  finalize,
  from,
  switchMap,
  take,
  throwError
} from 'rxjs';
import { AuthService } from '@educare/shared';
import { TranslateService } from '@ngx-translate/core';
import { Language, LocalStorage } from '@educare/shared';
import { UtilsService } from '@educare/shared';
import { apiConstants } from '../constants/api';
import { appConstants } from '../constants/app';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { SessionService } from '@educare/shared';
import { Router } from '@angular/router';

/** Only the "Your Face Registration is pending verification. Please Complete Face Verification to continue" error. */
function isFaceVerificationPendingError(error: HttpErrorResponse): boolean {
  if (error?.status !== 400) return false;
  const msg = (error?.error?.message ?? '').toLowerCase();
  const code = error?.error?.errorCode ?? '';
  return msg.includes('pending verification') || code === 'ECS-FR70101';
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly msalService = inject(MsalService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly utils = inject(UtilsService);
  private readonly sessionService = inject(SessionService);
  private readonly router = inject(Router);

  private isRefreshing = false;
  private refreshToken$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const userInfo = this.authService.getUserInfo();
    return next.handle(this.getHeader(request, userInfo?.access_token)).pipe(
      catchError((error: HttpErrorResponse) => {
        this.utils.spin.hide();
        // Face verification required: redirect without showing error message
        if (isFaceVerificationPendingError(error)) {
          this.router.navigate(['/face-verification']);
          return EMPTY;
        }
        switch (error.status) {
          case appConstants.errorCodes.Unauthorized:
            if (!request.url.includes('auth/logout')) {
              return this.handleTokenError(request, next);
            }
            return throwError(() => error);
          case appConstants.errorCodes.Forbidden:
            this.authService.logoutUser(true);
            return throwError(() => error);
          default:
            return throwError(() => error);
        }
      })
    );
  }

  handleTokenError(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshToken$.next(null);
      return from(this.refreshToken(environment.msalConfig.apiScopes)).pipe(
        switchMap((token: string) => {
          return this.setSession(request, next, token);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.authService.logoutUser(true);
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      return this.refreshToken$.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.getHeader(request, token)))
      );
    }
  }

  refreshToken(scopes: string[]): Promise<string> {
    return this.msalService.instance
      .initialize()
      .then(() => this.msalService.instance.acquireTokenSilent({ scopes }))
      .then((response) => {
        return this.setAccessToken(response.accessToken);
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          return this.msalService.instance
            .acquireTokenPopup({ scopes })
            .then((response) => {
              return this.setAccessToken(response.accessToken);
            })
            .catch((popupError) => {
              console.error('Token acquisition failed:', popupError?.errorCode || 'Unknown error');
              throw new Error('Authentication failed. Please try again.');
            });
        }
        console.error('Token refresh failed:', error?.errorCode || 'Unknown error');
        throw new Error('Authentication failed. Please try again.');
      });
  }

  getHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const currentLanguage = this.authService.getUserCurrentLanguage();
    return request.clone({
      setHeaders: {
        ...(token && { Authorization: `Bearer ${token}` }),
        language: this.translate.currentLang || currentLanguage || Language.English,
        type: appConstants.portalType
      }
    });
  }

  setAccessToken(accessToken: string): string {
    const userInfo = this.authService.getUserInfo();
    userInfo.access_token = accessToken;
    this.sessionService.setItem(LocalStorage.UserInfo, userInfo);
    return accessToken;
  }

  setSession(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<any> {
    return this.authService.post(this.utils.getEncryptedUrls(apiConstants.privateEndpoints.session), {}).pipe(
      switchMap((sessionResponse: any) => {
        if (sessionResponse?.data?.faceRegistration) {
          this.authService.updateFaceRegistration(sessionResponse.data.faceRegistration);
        }

        const userDetails = sessionResponse?.data?.userDetails;
        const profileStatus = sessionResponse?.data?.profileStatus;
        if (userDetails) {
          this.authService.getUser({
            ...userDetails,
            is_profile_completed: profileStatus?.isCompleted ?? false,
            is_email_verified: profileStatus?.isEmailVerified ?? false,
            is_external: profileStatus?.isExternal ?? false
          });
        }

        this.isRefreshing = false;
        this.refreshToken$.next(token);
        return next.handle(this.getHeader(request, token));
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
